import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { MIN_REASON } from '@urbanrats/ops-core';
import { Section } from '../components/common.tsx';
import { fmtTime, useCtx } from '../lib.ts';

type TargetKind = 'ALL' | 'OPERATOR' | 'BRAND' | 'SEGMENT' | 'PLAYERS';
interface Campaign {
  campaignId: string;
  name: string;
  note: string | null;
  status: string;
  targetKind: TargetKind;
  target: { operatorId?: string | null; brandId?: string | null; segment?: Record<string, unknown>; players?: number };
  reward: { kind: 'FREEBET' | 'BOOST'; count: number; values: Record<string, string>; boostBps: number | null; expiresDays: number };
  delivery: 'IMMEDIATE' | 'ON_LAUNCH';
  startsAt: string;
  endsAt: string;
  maxRecipients: number | null;
  budget: Record<string, string>;
  spent: Record<string, string>;
  recipients: number;
  funding: string;
  createdBy: string;
  requestedBy: string | null;
  approvedBy: string | null;
  createdAt: string;
}
interface ListView {
  me: string;
  canCreate: boolean;
  canApprove: boolean;
  campaigns: Campaign[];
  scopes: { operator_id: string; name: string; brand_id: string | null; brand_name: string | null; wallet_currency: string | null }[];
  currencies: { code: string; scale: number }[];
}
interface Preview {
  players: number;
  cappedByRecipients: number;
  byCurrency: Record<string, { players: number; cost: string; capped: number; noValue: boolean }>;
  unknownPlayers: string[];
}
type Server = NonNullable<ReturnType<typeof useCtx>['backend']['server']>;

/** "5.00" at scale 2 → "500" (no rounding: extra decimals are refused). */
const toAtomic = (s: string, scale: number): string | null => {
  const t = s.trim().replace(',', '.');
  if (!t) return null;
  const m = /^(\d+)(?:\.(\d+))?$/.exec(t);
  if (!m || (m[2] ?? '').length > scale) return null;
  return (BigInt(m[1]!) * 10n ** BigInt(scale) + BigInt((m[2] ?? '').padEnd(scale, '0') || '0')).toString();
};
const fromAtomic = (a: string, scale: number) => {
  const v = BigInt(a);
  const d = 10n ** BigInt(scale);
  return scale ? `${v / d}.${(v % d).toString().padStart(scale, '0')}` : v.toString();
};

const STATUS_TAG: Record<string, string> = { DRAFT: '', PENDING_APPROVAL: 'st-PENDING', ACTIVE: 'st-ACCEPTED', PAUSED: 'st-PENDING', ENDED: 'st-SETTLED', CANCELLED: 'st-VOIDED' };
const TARGETS: { kind: TargetKind; title: string; text: string }[] = [
  { kind: 'ALL', title: 'Everyone', text: 'every player of every operator' },
  { kind: 'OPERATOR', title: 'One operator', text: 'all players of one operator' },
  { kind: 'BRAND', title: 'One site', text: 'all players of one site' },
  { kind: 'SEGMENT', title: 'A player group', text: 'by country, activity, new or lapsed players' },
  { kind: 'PLAYERS', title: 'Chosen players', text: 'paste the operator’s player ids' },
];

/**
 * Promotion campaigns (backend): a step-by-step builder (who → what → when → limits → review with a live
 * estimate) and the list with progress. A campaign is a draft until someone else approves it.
 */
export function Campaigns() {
  const { backend } = useCtx();
  const server = backend.server!;
  const [v, setV] = useState<ListView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [building, setBuilding] = useState(false);
  const load = useCallback(async () => {
    const r = await server.get<ListView>('/v1/admin/campaigns');
    if (r.ok) {
      setV(r.value);
      setErr(null);
    } else setErr(r.error);
  }, [server]);
  useEffect(() => {
    const first = setTimeout(() => void load(), 0);
    const t = setInterval(() => void load(), 15_000);
    return () => (clearTimeout(first), clearInterval(t));
  }, [load]);
  if (!v) return <p className="empty">{err ?? 'Loading campaigns…'}</p>;
  const scaleOf = (code: string) => v.currencies.find((c) => c.code === code)?.scale ?? 2;
  return (
    <Section title="Campaigns" testId="campaigns">
      <p className="note">Give freebets or boosts to everyone, one operator, one site, a group of players or chosen players. Prizes land in the player’s rewards in their wallet currency. A campaign starts only after a second person approves it.</p>
      {v.canCreate && !building && (
        <button type="button" className="btn primary" onClick={() => setBuilding(true)} data-testid="cmp-new">
          + New campaign
        </button>
      )}
      {building && (
        <Builder
          v={v}
          server={server}
          scaleOf={scaleOf}
          done={async () => {
            setBuilding(false);
            await load();
          }}
        />
      )}
      <div className="cmp-list">
        {v.campaigns.length === 0 && !building && <p className="empty">No campaign yet.</p>}
        {v.campaigns.map((c) => (
          <CampaignCard key={c.campaignId} c={c} v={v} server={server} scaleOf={scaleOf} reload={load} />
        ))}
      </div>
    </Section>
  );
}

function F({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="op-field">
      <span>{label}</span>
      {children}
      {hint && <small className="muted">{hint}</small>}
    </label>
  );
}

const STEPS = ['Who', 'What', 'When', 'Limits', 'Review'] as const;

function Builder({ v, server, scaleOf, done }: { v: ListView; server: Server; scaleOf: (c: string) => number; done: () => Promise<void> }) {
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<TargetKind>('SEGMENT');
  const [t, setT] = useState({ operatorId: '', brandId: '', countries: '', activeDays: '', minRounds: '', newWithinDays: '', inactiveDays: '', players: '' });
  const [rw, setRw] = useState({ kind: 'FREEBET' as 'FREEBET' | 'BOOST', count: '1', boostPct: '10', expiresDays: '7' });
  const walletCurrencies = [...new Set(v.scopes.map((s) => s.wallet_currency).filter(Boolean))] as string[];
  const [values, setValues] = useState<Record<string, string>>({});
  const [when, setWhen] = useState({ delivery: 'IMMEDIATE' as 'IMMEDIATE' | 'ON_LAUNCH', startsAt: '', endsAt: '' });
  const [lim, setLim] = useState({ maxRecipients: '', funding: 'URBANRATS' });
  const [budget, setBudget] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const ops = [...new Map(v.scopes.map((s) => [s.operator_id, s.name])).entries()];
  const brands = v.scopes.filter((s) => s.brand_id && (!t.operatorId || s.operator_id === t.operatorId));
  const atomicMap = (m: Record<string, string>) => Object.fromEntries(Object.entries(m).flatMap(([k, x]) => (toAtomic(x, scaleOf(k)) ? [[k, toAtomic(x, scaleOf(k))!]] : [])));
  const body = () => ({
    name: name || 'Untitled campaign',
    note,
    targetKind: kind,
    target: {
      operatorId: t.operatorId || null,
      brandId: t.brandId || null,
      ...(kind === 'SEGMENT'
        ? { segment: { countries: t.countries.split(/[\s,]+/).filter(Boolean), activeDays: t.activeDays || null, minRounds: t.minRounds || null, newWithinDays: t.newWithinDays || null, inactiveDays: t.inactiveDays || null } }
        : {}),
      ...(kind === 'PLAYERS' ? { externalPlayerIds: t.players.split(/[\s,;]+/).filter(Boolean) } : {}),
    },
    reward: { kind: rw.kind, count: Number(rw.count) || 1, values: rw.kind === 'FREEBET' ? atomicMap(values) : {}, boostBps: rw.kind === 'BOOST' ? Math.round(Number(rw.boostPct) * 100) : null, expiresDays: Number(rw.expiresDays) || 7 },
    delivery: when.delivery,
    startsAt: when.startsAt ? new Date(when.startsAt).toISOString() : undefined,
    endsAt: when.endsAt ? new Date(when.endsAt).toISOString() : undefined,
    maxRecipients: lim.maxRecipients || null,
    budget: atomicMap(budget),
    funding: lim.funding,
  });
  const runPreview = async () => {
    const r = await server.post<Preview>('/v1/admin/campaigns/preview', body());
    if (r.ok) {
      setPreview(r.value);
      setErr(null);
    } else {
      setPreview(null);
      setErr(r.error);
    }
  };
  useEffect(() => {
    if (step !== 4) return;
    const tm = setTimeout(() => void runPreview(), 0);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when the review step opens
  }, [step]);
  const stepOk = [
    kind === 'ALL' || ((kind !== 'OPERATOR' && kind !== 'PLAYERS') || !!t.operatorId) && (kind !== 'BRAND' || !!t.brandId) && (kind !== 'PLAYERS' || !!t.players.trim()),
    rw.kind === 'BOOST' ? Number(rw.boostPct) > 0 && Number(rw.boostPct) <= 25 : Object.keys(atomicMap(values)).length > 0,
    !when.endsAt || !when.startsAt || new Date(when.endsAt) > new Date(when.startsAt),
    true,
    name.trim().length >= 2 && reason.trim().length >= MIN_REASON,
  ];
  return (
    <div className="cmp-builder" data-testid="cmp-builder">
      <ol className="cmp-steps">
        {STEPS.map((s, i) => (
          <li key={s} className={i === step ? 'on' : i < step ? 'done' : ''}>
            <button type="button" onClick={() => i <= step && setStep(i)} disabled={i > step}>
              <b>{i + 1}</b> {s}
            </button>
          </li>
        ))}
      </ol>
      {step === 0 && (
        <div className="cmp-pane">
          <h4>Who gets it?</h4>
          <div className="cmp-cards">
            {TARGETS.map((x) => (
              <button key={x.kind} type="button" className={`cmp-card ${kind === x.kind ? 'on' : ''}`} onClick={() => setKind(x.kind)} data-testid={`cmp-target-${x.kind}`}>
                <strong>{x.title}</strong>
                <span className="muted">{x.text}</span>
              </button>
            ))}
          </div>
          {kind !== 'ALL' && (
            <div className="op-grid">
              <F label={kind === 'SEGMENT' ? 'Operator (optional)' : 'Operator'}>
                <select value={t.operatorId} onChange={(e) => setT({ ...t, operatorId: e.target.value, brandId: '' })} data-testid="cmp-operator">
                  <option value="">{kind === 'SEGMENT' ? 'every operator' : 'choose…'}</option>
                  {ops.map(([id, n]) => (
                    <option key={id} value={id}>
                      {n} · {id}
                    </option>
                  ))}
                </select>
              </F>
              {(kind === 'BRAND' || kind === 'SEGMENT') && (
                <F label={kind === 'SEGMENT' ? 'Site (optional)' : 'Site'}>
                  <select value={t.brandId} onChange={(e) => setT({ ...t, brandId: e.target.value })} disabled={!t.operatorId}>
                    <option value="">{kind === 'SEGMENT' ? 'every site' : 'choose…'}</option>
                    {brands.map((b) => (
                      <option key={b.brand_id} value={b.brand_id!}>
                        {b.brand_name} · {b.brand_id} ({b.wallet_currency})
                      </option>
                    ))}
                  </select>
                </F>
              )}
            </div>
          )}
          {kind === 'SEGMENT' && (
            <div className="op-grid">
              <F label="Countries" hint="player's declared country, e.g. TR DE — empty = any">
                <input value={t.countries} onChange={(e) => setT({ ...t, countries: e.target.value.toUpperCase() })} placeholder="TR DE" />
              </F>
              <F label="Played in the last … days" hint="active players">
                <input inputMode="numeric" value={t.activeDays} onChange={(e) => setT({ ...t, activeDays: e.target.value })} placeholder="7" />
              </F>
              <F label="At least … rounds" hint="in that period (or ever)">
                <input inputMode="numeric" value={t.minRounds} onChange={(e) => setT({ ...t, minRounds: e.target.value })} placeholder="20" />
              </F>
              <F label="New players: first seen in the last … days">
                <input inputMode="numeric" value={t.newWithinDays} onChange={(e) => setT({ ...t, newWithinDays: e.target.value })} placeholder="3" />
              </F>
              <F label="Lapsed players: no play for … days" hint="win-back">
                <input inputMode="numeric" value={t.inactiveDays} onChange={(e) => setT({ ...t, inactiveDays: e.target.value })} placeholder="14" />
              </F>
            </div>
          )}
          {kind === 'PLAYERS' && (
            <F label="Player ids of this operator" hint="one per line or separated by commas — the ids the operator sends at launch">
              <textarea rows={5} value={t.players} onChange={(e) => setT({ ...t, players: e.target.value })} placeholder={'player-10293\nplayer-10294'} data-testid="cmp-players" />
            </F>
          )}
        </div>
      )}
      {step === 1 && (
        <div className="cmp-pane">
          <h4>What do they get?</h4>
          <div className="cmp-cards">
            <button type="button" className={`cmp-card ${rw.kind === 'FREEBET' ? 'on' : ''}`} onClick={() => setRw({ ...rw, kind: 'FREEBET' })}>
              <strong>Freebet</strong>
              <span className="muted">a free stake; the player keeps the profit if it wins</span>
            </button>
            <button type="button" className={`cmp-card ${rw.kind === 'BOOST' ? 'on' : ''}`} onClick={() => setRw({ ...rw, kind: 'BOOST' })}>
              <strong>Win boost</strong>
              <span className="muted">a higher payout on one winning bet</span>
            </button>
          </div>
          <div className="op-grid">
            <F label="How many per player">
              <input inputMode="numeric" value={rw.count} onChange={(e) => setRw({ ...rw, count: e.target.value })} />
            </F>
            {rw.kind === 'BOOST' && (
              <F label="Boost (%)" hint="1–25 %">
                <input inputMode="decimal" value={rw.boostPct} onChange={(e) => setRw({ ...rw, boostPct: e.target.value })} />
              </F>
            )}
            <F label="Valid for (days)" hint="unused rewards expire">
              <input inputMode="numeric" value={rw.expiresDays} onChange={(e) => setRw({ ...rw, expiresDays: e.target.value })} />
            </F>
          </div>
          {rw.kind === 'FREEBET' && (
            <>
              <p className="note">Freebet value per wallet currency — a player whose currency has no value gets nothing (we never convert).</p>
              <div className="op-grid">
                {walletCurrencies.map((c) => (
                  <F key={c} label={`Value in ${c}`} hint={`up to ${scaleOf(c)} decimals`}>
                    <input inputMode="decimal" value={values[c] ?? ''} onChange={(e) => setValues({ ...values, [c]: e.target.value })} placeholder={c === 'EUR' || c === 'USD' ? '5.00' : ''} data-testid={`cmp-value-${c}`} />
                  </F>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {step === 2 && (
        <div className="cmp-pane">
          <h4>When?</h4>
          <div className="cmp-cards">
            <button type="button" className={`cmp-card ${when.delivery === 'IMMEDIATE' ? 'on' : ''}`} onClick={() => setWhen({ ...when, delivery: 'IMMEDIATE' })}>
              <strong>At the start</strong>
              <span className="muted">everyone who matches when the campaign starts</span>
            </button>
            <button type="button" className={`cmp-card ${when.delivery === 'ON_LAUNCH' ? 'on' : ''}`} onClick={() => setWhen({ ...when, delivery: 'ON_LAUNCH' })}>
              <strong>When they open the game</strong>
              <span className="muted">each matching player at their next launch during the campaign (good for welcome and win-back)</span>
            </button>
          </div>
          <div className="op-grid">
            <F label="Starts" hint="empty = right after approval">
              <input type="datetime-local" value={when.startsAt} onChange={(e) => setWhen({ ...when, startsAt: e.target.value })} />
            </F>
            <F label="Ends" hint="empty = 7 days after the start">
              <input type="datetime-local" value={when.endsAt} onChange={(e) => setWhen({ ...when, endsAt: e.target.value })} />
            </F>
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="cmp-pane">
          <h4>Limits and who pays</h4>
          <div className="op-grid">
            <F label="At most … players" hint="empty = no limit">
              <input inputMode="numeric" value={lim.maxRecipients} onChange={(e) => setLim({ ...lim, maxRecipients: e.target.value })} />
            </F>
            <F label="Who pays">
              <select value={lim.funding} onChange={(e) => setLim({ ...lim, funding: e.target.value })}>
                <option value="URBANRATS">UrbanRats (credited to the operator)</option>
                <option value="OPERATOR">The operator</option>
              </select>
            </F>
          </div>
          {rw.kind === 'FREEBET' && (
            <>
              <p className="note">Budget per currency (total freebet value). Delivery stops when it is used up; empty = no budget limit.</p>
              <div className="op-grid">
                {walletCurrencies.map((c) => (
                  <F key={c} label={`Budget in ${c}`}>
                    <input inputMode="decimal" value={budget[c] ?? ''} onChange={(e) => setBudget({ ...budget, [c]: e.target.value })} />
                  </F>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {step === 4 && (
        <div className="cmp-pane">
          <h4>Review</h4>
          <div className="cmp-estimate" data-testid="cmp-estimate">
            {!preview ? (
              <p className="muted">{err ?? 'Counting matching players…'}</p>
            ) : (
              <>
                <p>
                  <b>{preview.players}</b> player(s) match right now{preview.cappedByRecipients ? `; ${preview.cappedByRecipients} over the player limit` : ''}.
                  {when.delivery === 'ON_LAUNCH' && ' Delivered as they open the game, so the final number can differ.'}
                </p>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Wallet currency</th>
                        <th>Players</th>
                        <th>Cost</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(preview.byCurrency).map(([c, e]) => (
                        <tr key={c}>
                          <td className="mono">{c}</td>
                          <td>{e.players}</td>
                          <td className="mono">{rw.kind === 'FREEBET' ? `${fromAtomic(e.cost, scaleOf(c))} ${c}` : 'boost (no face value)'}</td>
                          <td className="muted">{e.noValue ? 'no value set for this currency — skipped' : e.capped ? `${e.capped} over the budget` : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.unknownPlayers.length > 0 && <p className="op-msg bad">Unknown player ids (never played with this operator): {preview.unknownPlayers.join(', ')}</p>}
              </>
            )}
          </div>
          <div className="op-grid">
            <F label="Campaign name">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Hoş geldin freebet" data-testid="cmp-name" />
            </F>
            <F label="Internal note (optional)">
              <input value={note} onChange={(e) => setNote(e.target.value)} />
            </F>
            <F label={`Reason (min ${MIN_REASON} chars)`}>
              <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cmp-reason" />
            </F>
          </div>
        </div>
      )}
      {err && step !== 4 && <p className="op-msg bad">{err}</p>}
      <div className="op-actions cmp-nav">
        <button type="button" className="btn ghost" onClick={() => void done()}>
          Cancel
        </button>
        {step > 0 && (
          <button type="button" className="btn" onClick={() => setStep(step - 1)}>
            ← Back
          </button>
        )}
        {step < 4 ? (
          <button type="button" className="btn primary" disabled={!stepOk[step]} onClick={() => setStep(step + 1)} data-testid="cmp-next">
            Next →
          </button>
        ) : (
          <button
            type="button"
            className="btn primary"
            disabled={!stepOk[4]}
            data-testid="cmp-create"
            onClick={async () => {
              const r = await server.post<{ campaignId: string }>('/v1/admin/campaigns', { ...body(), reason });
              if (!r.ok) return setErr(r.error);
              await done();
            }}
          >
            Save as draft
          </button>
        )}
      </div>
    </div>
  );
}

function CampaignCard({ c, v, server, scaleOf, reload }: { c: Campaign; v: ListView; server: Server; scaleOf: (code: string) => number; reload: () => Promise<void> }) {
  const [msg, setMsg] = useState<string | null>(null);
  const [grants, setGrants] = useState<{ alias: string; operatorId: string; externalPlayerId: string; grantedAt: string; currency: string; faceAtomic: string; items: number }[] | null>(null);
  const act = async (path: string, label: string) => {
    const reason = prompt(`${label} “${c.name}”. Reason (min ${MIN_REASON} chars):`);
    if (!reason || reason.trim().length < MIN_REASON) return;
    const r = await server.post(`/v1/admin/campaigns/${c.campaignId}/${path}`, { reason });
    setMsg(r.ok ? null : r.error);
    await reload();
  };
  const who =
    c.targetKind === 'ALL'
      ? 'Everyone'
      : c.targetKind === 'OPERATOR'
        ? `Operator ${c.target.operatorId}`
        : c.targetKind === 'BRAND'
          ? `Site ${c.target.operatorId}/${c.target.brandId}`
          : c.targetKind === 'PLAYERS'
            ? `${c.target.players} chosen player(s) of ${c.target.operatorId}`
            : `Group${c.target.operatorId ? ` in ${c.target.operatorId}${c.target.brandId ? `/${c.target.brandId}` : ''}` : ''}: ${Object.entries(c.target.segment ?? {})
                .filter(([, x]) => x !== null && !(Array.isArray(x) && x.length === 0))
                .map(([k, x]) => `${k} ${Array.isArray(x) ? x.join(',') : String(x)}`)
                .join(' · ')}`;
  const what = c.reward.kind === 'FREEBET' ? `${c.reward.count} × freebet (${Object.entries(c.reward.values).map(([k, x]) => `${fromAtomic(x, scaleOf(k))} ${k}`).join(', ')})` : `${c.reward.count} × +${(c.reward.boostBps ?? 0) / 100} % boost`;
  const mine = c.createdBy === v.me || c.requestedBy === v.me;
  return (
    <div className="cmp-item" data-testid={`cmp-${c.campaignId}`}>
      <div className="cmp-item-head">
        <strong>{c.name}</strong>
        <span className={`tag ${STATUS_TAG[c.status] ?? ''}`}>{c.status.replace('_', ' ')}</span>
      </div>
      <div className="cmp-item-grid">
        <span>
          <small className="muted">Who</small>
          {who}
        </span>
        <span>
          <small className="muted">What</small>
          {what} · valid {c.reward.expiresDays} d
        </span>
        <span>
          <small className="muted">When</small>
          {c.delivery === 'IMMEDIATE' ? 'at the start' : 'at the next launch'} · {fmtTime(c.startsAt)} → {fmtTime(c.endsAt)}
        </span>
        <span>
          <small className="muted">Progress</small>
          {c.recipients}
          {c.maxRecipients ? ` / ${c.maxRecipients}` : ''} player(s)
          {Object.keys(c.spent).length > 0 && ` · ${Object.entries(c.spent).map(([k, x]) => `${fromAtomic(x, scaleOf(k))}${c.budget[k] ? ` / ${fromAtomic(c.budget[k]!, scaleOf(k))}` : ''} ${k}`).join(' · ')}`}
        </span>
      </div>
      <div className="muted cmp-item-meta">
        by {c.createdBy}
        {c.approvedBy ? ` · approved by ${c.approvedBy}` : ''} · {c.funding === 'URBANRATS' ? 'UrbanRats pays' : 'operator pays'}
      </div>
      <div className="op-actions">
        {c.status === 'DRAFT' && v.canCreate && (
          <button type="button" className="btn primary" onClick={() => void act('request', 'Send for approval:')} data-testid={`cmp-request-${c.campaignId}`}>
            Send for approval
          </button>
        )}
        {c.status === 'PENDING_APPROVAL' &&
          v.canApprove &&
          (mine ? (
            <span className="muted">Waiting for someone else to approve.</span>
          ) : (
            <>
              <button type="button" className="btn primary" onClick={() => void act('approve', 'Approve and start')}>
                Approve and start
              </button>
              <button type="button" className="btn danger" onClick={() => void act('reject', 'Send back to draft')}>
                Reject
              </button>
            </>
          ))}
        {c.status === 'ACTIVE' && v.canCreate && (
          <button type="button" className="btn" onClick={() => void act('pause', 'Pause')}>
            Pause
          </button>
        )}
        {c.status === 'PAUSED' && v.canCreate && (
          <button type="button" className="btn" onClick={() => void act('resume', 'Resume')}>
            Resume
          </button>
        )}
        {['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'PAUSED'].includes(c.status) && v.canCreate && (
          <button type="button" className="btn ghost" onClick={() => void act('cancel', 'Cancel')}>
            Cancel campaign
          </button>
        )}
        {c.recipients > 0 && (
          <button
            type="button"
            className="btn ghost"
            onClick={async () => {
              if (grants) return setGrants(null);
              const r = await server.get<{ grants: NonNullable<typeof grants> }>(`/v1/admin/campaigns/${c.campaignId}/grants`);
              if (r.ok) setGrants(r.value.grants);
            }}
          >
            {grants ? 'Hide recipients' : 'Recipients'}
          </button>
        )}
        {msg && <span className="op-msg bad">{msg}</span>}
      </div>
      {grants && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Player</th>
                <th>Operator id</th>
                <th>When</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => (
                <tr key={`${g.operatorId}/${g.externalPlayerId}`}>
                  <td>{g.alias}</td>
                  <td className="mono">
                    {g.operatorId}/{g.externalPlayerId}
                  </td>
                  <td className="muted">{fmtTime(g.grantedAt)}</td>
                  <td className="mono">{g.faceAtomic !== '0' ? `${fromAtomic(g.faceAtomic, scaleOf(g.currency))} ${g.currency}` : `${g.items} boost(s)`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
